import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {TagCombobox} from '@/components/tag-combobox'
import {CatalogOtherField} from '@/components/catalog-other-field'
import {useTags} from '@/hooks/empresa/organizacion/use-tags'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'

/** Debe quedar al menos un campo editable (área, cargo o sede); «nombres» no se edita acá. */
function validateCamposEditables(campos: string[]): boolean {
  return campos.some((campo) => campo === 'areas' || campo === 'positions' || campo === 'sede')
}

/** Completa, sobre el 409 `roster_incompleto_bateria`, los campos que faltan sin salir a Empleados. */
export function CompletarRosterDialog({
  businessId,
  employeeId,
  campos,
  open,
  onOpenChange,
  onCompleted,
}: {
  businessId: string
  employeeId: string
  campos: string[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onCompleted: () => void
}) {
  const {t} = useTranslation()
  const queryClient = useQueryClient()
  const {data: roster} = useAllEmployees(open ? businessId : undefined)
  const employee: Employee | undefined = roster?.find((e) => e.id === employeeId)
  const {data: positions} = useTags(businessId, 'positions', open)
  const {data: areas} = useTags(businessId, 'areas', open)
  const {data: sedes} = useTags(businessId, 'sedes', open)

  const [areaNames, setAreaNames] = useState<string[]>([])
  const [positionNames, setPositionNames] = useState<string[]>([])
  const [sede, setSede] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!employee) return
    setAreaNames(employee.areas.map((a) => a.name))
    setPositionNames(employee.positions.map((p) => p.name))
    setSede(employee.sede?.name ?? '')
  }, [employee])

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {}
      if (campos.includes('areas')) body.areas = areaNames
      if (campos.includes('positions')) body.positions = positionNames
      if (campos.includes('sede')) body.sede = sede
      return (await api.patch(`/businesses/${businessId}/employees/${employeeId}`, body)).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['tags', businessId]})
      onCompleted()
      close()
    },
    onError: (err) => setError(translateApiError(err)),
  })

  function close() {
    setError(null)
    mutation.reset()
    onOpenChange(false)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    mutation.mutate()
  }

  // El nombre no se edita acá: la identidad tiene sus propias reglas y se remite a Empleados.
  const nombresIncompletos = campos.includes('nombres')
  const nadaQueEditar = !validateCamposEditables(campos)

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="completar-roster-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('admin.bateria.rosterIncompleto.title', {
              nombre: employee ? `${employee.name} ${employee.last_name}` : '',
            })}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">{t('admin.bateria.rosterIncompleto.intro')}</p>
          {nombresIncompletos && (
            <p className="notice-amber rounded-lg px-3 py-2 text-xs" data-testid="completar-roster-nombres-aviso">
              {t('admin.bateria.rosterIncompleto.nombresAviso')}
            </p>
          )}
          {campos.includes('areas') && (
            <TagCombobox
              label={t('admin.bateria.rosterIncompleto.campo.areas')}
              testId="completar-roster-area"
              available={areas?.items ?? []}
              value={areaNames}
              onChange={setAreaNames}
            />
          )}
          {campos.includes('positions') && (
            <TagCombobox
              label={t('admin.bateria.rosterIncompleto.campo.positions')}
              testId="completar-roster-cargo"
              available={positions?.items ?? []}
              value={positionNames}
              onChange={setPositionNames}
            />
          )}
          {campos.includes('sede') && (
            <CatalogOtherField
              id="completar-roster-sede"
              label={t('admin.bateria.rosterIncompleto.campo.sede')}
              options={(sedes?.items ?? []).map((s) => s.name)}
              value={sede}
              onChange={setSede}
              onCommit={setSede}
            />
          )}
          <FormDialogFooter
            error={error}
            pending={mutation.isPending || nadaQueEditar}
            submitLabel={t('admin.bateria.rosterIncompleto.submit')}
            onCancel={close}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
